/**
 * A dashboard's ship and range are its own.
 *
 * They live in the document - the `ship` and `window` variables
 * (`variables.ts`) - and the toolbar writes them through
 * `dashboard-selection.ts`. Explore is Explore and a dashboard is a dashboard:
 * neither moves the other.
 *
 * Until 2026-10-05 a dashboard could instead *follow* Explore (an "attached"
 * dashboard, `binding: 'url'`): its toolbar edited Explore's atoms and the
 * address bar, and what it showed depended on what the viewer last looked at.
 * That mode is gone - loading converts it (`normalizeVariable`) - because the
 * two-way coupling kept leaking state between the sections.
 *
 * What is left is one courtesy: a dashboard that has no ship or window yet
 * starts on the selection Explore has when it is first opened, so a new or
 * converted dashboard is never empty. It is a copy, taken once.
 */

import { activePreset, endDate, plottedShipIds, startDate } from '../../state/store.ts';
import { dashboards, type Dashboard } from './dashboard-state.ts';
import { SHIP_VAR, WINDOW_VAR, type DashboardVariable, type TimeWindow } from './variables.ts';

function variable(dashboard: Dashboard, id: string): DashboardVariable | undefined {
  return dashboard.variables.find((v) => v.id === id);
}

/** Explore's window as a stored value: a preset if one is active, else the pair. */
export function currentWindow(): TimeWindow {
  return activePreset.value && activePreset.value !== 'custom'
    ? { preset: activePreset.value }
    : { start: startDate.value, end: endDate.value };
}

function hasShips(v: DashboardVariable | undefined): boolean {
  const value = v?.current ?? v?.default;
  return Array.isArray(value) && value.some(Boolean);
}

function hasWindow(v: DashboardVariable | undefined): boolean {
  const value = v?.current ?? v?.default;
  return !!value && !Array.isArray(value) && !!(value.preset || (value.start && value.end));
}

/**
 * Give a dashboard with no ship or window Explore's, once. Not an edit: the
 * dashboard is not marked unsaved, and Save is what would make it everyone's.
 * True when it took something.
 */
export function adoptExploreSelection(dashboard: Dashboard): boolean {
  let took = false;
  const ships = variable(dashboard, SHIP_VAR);
  if (ships && !hasShips(ships) && plottedShipIds().length) {
    ships.current = plottedShipIds();
    took = true;
  }
  const window = variable(dashboard, WINDOW_VAR);
  if (window && !hasWindow(window) && startDate.value && endDate.value) {
    window.current = currentWindow();
    took = true;
  }
  if (took) dashboards.notify();
  return took;
}
